const TOKEN_KEY = "mew_google_token";
const EXPIRY_KEY = "mew_google_token_expiry";
// Caches the signed-in user's identity (email used to resolve their manager/fieldworker
// tier from the shared sheet's Members tab; name/picture shown in the account menu).
// Auth-scoped, not project data — preserved on project switch but cleared on sign-out.
const EMAIL_KEY = "mew_user_email";
const NAME_KEY = "mew_user_name";
const PICTURE_KEY = "mew_user_picture";
// Requested scopes are all NON-sensitive (no Google OAuth verification):
//  - drive.file: per-file access to files MewMaps creates or the user opens with the app.
//    The Sheets/Forms APIs honor it for app-created files, all MewMaps ever touches.
//  - openid/email: identify the signed-in user so their tier can be looked up in the
//    project's Members tab.
//  - profile: name + picture for the Google-style account avatar/menu.
//  No directory/group scopes (those are restricted and need a Workspace admin), so group
//  membership is intentionally NOT used for tiering.
const SCOPES = [
  "https://www.googleapis.com/auth/drive.file",
  "openid",
  "email",
  "profile",
].join(" ");

const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

interface OAuthMessage {
  source?: string;
  error?: string;
  error_description?: string;
  state?: string;
  access_token?: string;
  expires_in?: string | number;
}

const listeners = new Set<(token: string | null) => void>();

function emit(): void {
  listeners.forEach((l) => {
    try { l(getToken()); } catch (e) { console.error(e); }
  });
}

export function onAuthChange(cb: (token: string | null) => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function getToken(): string | null {
  const token = localStorage.getItem(TOKEN_KEY);
  const expiry = parseInt(localStorage.getItem(EXPIRY_KEY) || "0", 10);
  if (!token) return null;
  if (expiry && Date.now() > expiry - 30_000) return null;
  return token;
}

export function isSignedIn(): boolean {
  return !!getToken();
}

export function signOut(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(EXPIRY_KEY);
  localStorage.removeItem(EMAIL_KEY);
  localStorage.removeItem(NAME_KEY);
  localStorage.removeItem(PICTURE_KEY);
  emit();
}

export interface UserProfile {
  email: string | null;
  name: string | null;
  picture: string | null;
}

/** Cached email of the signed-in user, or null if not yet resolved. */
export function getUserEmail(): string | null {
  return localStorage.getItem(EMAIL_KEY);
}

/** Cached identity of the signed-in user (whatever has been resolved so far). */
export function getUserProfile(): UserProfile {
  return {
    email: localStorage.getItem(EMAIL_KEY),
    name: localStorage.getItem(NAME_KEY),
    picture: localStorage.getItem(PICTURE_KEY),
  };
}

/**
 * Resolves the signed-in user's identity (email + name + picture) via the OpenID userinfo
 * endpoint and caches it. Best-effort: on any failure returns whatever is already cached so
 * callers can fall back to safe defaults rather than blocking the app. Short-circuits once
 * email AND name are cached (name only arrives with the profile scope, so this also forces
 * a refresh for users who were cached under the earlier email-only scope set).
 */
export async function resolveUserProfile(force = false): Promise<UserProfile> {
  if (!force && getUserEmail() && localStorage.getItem(NAME_KEY)) {
    return getUserProfile();
  }
  try {
    const res = await fetchWithAuth("https://www.googleapis.com/oauth2/v3/userinfo");
    if (!res.ok) return getUserProfile();
    const data = (await res.json()) as { email?: string; name?: string; picture?: string };
    const email = (data.email || "").trim().toLowerCase();
    if (email) localStorage.setItem(EMAIL_KEY, email);
    if (data.name) localStorage.setItem(NAME_KEY, data.name);
    if (data.picture) localStorage.setItem(PICTURE_KEY, data.picture);
    return getUserProfile();
  } catch {
    return getUserProfile();
  }
}

/** Convenience wrapper: resolves the full profile but returns just the email. */
export async function resolveUserEmail(force = false): Promise<string | null> {
  const profile = await resolveUserProfile(force);
  return profile.email;
}

let _signInPromise: Promise<string> | null = null;

export function signIn(): Promise<string> {
  if (_signInPromise) return _signInPromise;
  _signInPromise = new Promise<string>((resolve, reject) => {
    if (!CLIENT_ID) {
      reject(new Error("REACT_APP_GOOGLE_CLIENT_ID is not configured. Copy .env.example to .env and set it."));
      return;
    }
    const state = Math.random().toString(36).slice(2);
    const redirectUri = `${window.location.origin}/redirect.html`;
    const url =
      "https://accounts.google.com/o/oauth2/v2/auth?" +
      new URLSearchParams({
        client_id: CLIENT_ID,
        redirect_uri: redirectUri,
        response_type: "token",
        scope: SCOPES,
        include_granted_scopes: "true",
        state,
        prompt: "consent",
      }).toString();

    const w = 500, h = 600;
    const left = window.screenX + (window.outerWidth - w) / 2;
    const top = window.screenY + (window.outerHeight - h) / 2;
    const popup = window.open(url, "mew_oauth", `width=${w},height=${h},left=${left},top=${top}`);
    if (!popup) {
      reject(new Error("ポップアップがブロックされました。ブラウザでポップアップを許可してください。"));
      return;
    }

    let resolved = false;
    const onMessage = (event: MessageEvent<unknown>) => {
      if (event.origin !== window.location.origin) return;
      const data = (event.data || {}) as OAuthMessage;
      if (data.source !== "mew-oauth") return;
      window.removeEventListener("message", onMessage);
      clearInterval(closeWatcher);
      if (data.error) {
        resolved = true;
        reject(new Error(`OAuth エラー: ${data.error}${data.error_description ? " — " + data.error_description : ""}`));
        return;
      }
      if (data.state !== state) {
        resolved = true;
        reject(new Error("OAuth state mismatch"));
        return;
      }
      if (!data.access_token) {
        resolved = true;
        reject(new Error("OAuth: アクセストークンを取得できませんでした"));
        return;
      }
      const expiresIn = parseInt(String(data.expires_in || "3600"), 10);
      localStorage.setItem(TOKEN_KEY, data.access_token);
      localStorage.setItem(EXPIRY_KEY, String(Date.now() + expiresIn * 1000));
      resolved = true;
      emit();
      resolve(data.access_token);
    };
    window.addEventListener("message", onMessage);
    const closeWatcher = setInterval(() => {
      if (popup.closed && !resolved) {
        clearInterval(closeWatcher);
        window.removeEventListener("message", onMessage);
        reject(new Error("認証ウィンドウが閉じられました"));
      }
    }, 500);
  }).finally(() => { _signInPromise = null; });
  return _signInPromise;
}

export async function ensureToken(): Promise<string> {
  const t = getToken();
  if (t) return t;
  return signIn();
}

let throttlePromise: Promise<void> = Promise.resolve();
let lastRequestAt = 0;
const MIN_INTERVAL_MS = 100;

async function throttle(): Promise<void> {
  const next = throttlePromise.then(async () => {
    const dt = Date.now() - lastRequestAt;
    if (dt < MIN_INTERVAL_MS) {
      await new Promise<void>((r) => setTimeout(r, MIN_INTERVAL_MS - dt));
    }
    lastRequestAt = Date.now();
  });
  throttlePromise = next.catch(() => {});
  return next;
}

export async function fetchWithAuth(
  url: string,
  options: RequestInit = {},
  retryCount = 0
): Promise<Response> {
  await throttle();
  let token = await ensureToken();
  const headers = { ...(options.headers as Record<string, string> || {}), Authorization: `Bearer ${token}` };
  let response = await fetch(url, { ...options, headers });

  if (response.status === 401) {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(EXPIRY_KEY);
    token = await signIn();
    response = await fetch(url, { ...options, headers: { ...headers, Authorization: `Bearer ${token}` } });
  }

  if (response.status === 429) {
    if (retryCount > 5) throw new Error("Google API: rate-limit exceeded");
    const retryAfter = response.headers.get("Retry-After");
    const wait = retryAfter ? parseInt(retryAfter, 10) * 1000 : 2000 * Math.pow(2, retryCount);
    await new Promise<void>((r) => setTimeout(r, wait));
    return fetchWithAuth(url, options, retryCount + 1);
  }
  return response;
}
